import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useToast } from '../../context/ToastContext';
import {
  FiSearch, FiRefreshCw, FiX,
  FiChevronLeft, FiChevronRight, FiThumbsUp, FiThumbsDown,
  FiMessageSquare,
} from 'react-icons/fi';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import { Skeleton } from 'primereact/skeleton';
import '../commissionscan/CommissionScan.css';
import '../../styles/Voice.css';
import '../../styles/QuoteProposal.css';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Success: { label: 'Success', color: '#34d399' },
  Failed:  { label: 'Failed',  color: '#f87171' },
};

const getToken    = () => localStorage.getItem('toolboxToken') || '';
const getNcToken  = () => localStorage.getItem('amsToken')     || '';

type PreviewTab = 'insured' | 'coverage' | 'drivers' | 'vehicles';

interface HistoryRow {
  submissionId:     string;
  fileName:         string;
  created:          string;
  userName:         string;
  updateInNowcerts: string;
  status:           string;
  documentType:     string;
  responseJson:     string | null;
  isLiked:          boolean | null;
  totalCount:       number;
}

const PAGE_SIZE = 10;

const MTMScanHistory: React.FC = () => {
  const [historyData,  setHistoryData]  = useState<HistoryRow[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [page,         setPage]         = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  /* ── Preview dialog ── */
  const [showPopup,        setShowPopup]        = useState(false);
  const [previewLoading,   setPreviewLoading]   = useState(false);
  const [pdfUrl,           setPdfUrl]           = useState('');
  const [activeRow,        setActiveRow]        = useState<HistoryRow | null>(null);
  const [previewTab,       setPreviewTab]       = useState<PreviewTab>('insured');

  /* ── Extracted data for preview ── */
  const [insuredName,     setInsuredName]     = useState('');
  const [address,         setAddress]         = useState('');
  const [city,            setCity]            = useState('');
  const [stateName,       setStateName]       = useState('');
  const [zipCode,         setZipCode]         = useState('');
  const [policyNumber,    setPolicyNumber]    = useState('');
  const [policyPremium,   setPolicyPremium]   = useState('');
  const [effectiveDate,   setEffectiveDate]   = useState('');
  const [expirationDate,  setExpirationDate]  = useState('');
  const [coverage,        setCoverage]        = useState<any>(null);
  const [drivers,         setDrivers]         = useState<any[]>([]);
  const [vehicles,        setVehicles]        = useState<any[]>([]);
  const [docType,         setDocType]         = useState('');

  /* ── Feedback ── */
  const [feedbackMap,      setFeedbackMap]      = useState<Record<string, boolean | null>>({});
  const [feedbackTxId,     setFeedbackTxId]     = useState<string | null>(null);
  const [feedbackText,     setFeedbackText]     = useState('');
  const [feedbackRating,   setFeedbackRating]   = useState<boolean | null>(null);
  const [feedbackAnchor,   setFeedbackAnchor]   = useState<{ top: number; right: number } | null>(null);
  const [fbSubmitting,     setFbSubmitting]     = useState(false);
  const [fbError,          setFbError]          = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  /* ── Upload to AMS dialog ── */
  const [showUploadDialog,    setShowUploadDialog]    = useState(false);
  const [uploadClientSearch,  setUploadClientSearch]  = useState('');
  const [uploadClientName,    setUploadClientName]    = useState('');
  const [uploadClientList,    setUploadClientList]    = useState<any[]>([]);
  const [uploadClientLoading, setUploadClientLoading] = useState(false);
  const [uploadInsuredId,     setUploadInsuredId]     = useState('');
  const [uploadLoading,       setUploadLoading]       = useState(false);

  /* ── Toast ── */
  const { showSuccess, showError } = useToast();

  const [transactionId, setTransactionId] = useState('');
  const [fileName,      setFileName]      = useState('');

  /* ── Fetch history ── */
  const fetchHistory = useCallback(() => {
    setLoading(true);
    axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/MTMScan/history`, {
      params: { pageNumber: page, recordCount: PAGE_SIZE },
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        const rows = res.data || [];
        setHistoryData(rows);
        setTotalRecords(rows.length > 0 ? rows[0].totalCount : 0);
        const map: Record<string, boolean | null> = {};
        rows.forEach((r: HistoryRow) => { map[r.submissionId] = r.isLiked ?? null; });
        setFeedbackMap(map);
      })
      .catch(() => showError('Error loading history.'))
      .finally(() => setLoading(false));
  }, [page, showError]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  /* ── AMS upload client search ── */
  useEffect(() => {
    if (!uploadClientSearch.trim()) { setUploadClientList([]); return; }
    const t = setTimeout(() => {
      setUploadClientLoading(true);
      axios.get(`${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/customerlist`, {
        params: { searchText: uploadClientSearch, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToken()}` },
      })
        .then(r => setUploadClientList(r.data || []))
        .finally(() => setUploadClientLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [uploadClientSearch]);

  /* ── Preview ── */
  const onPreviewClick = useCallback((row: HistoryRow) => {
    setActiveRow(row);
    setTransactionId(row.submissionId);
    setFileName(row.fileName);
    setDocType(row.documentType || 'Binder');
    setPreviewTab('insured');

    try {
      const parsed      = JSON.parse(row.responseJson || '{}');
      const data        = parsed?.ResponseJson?.[0] || {};

      setInsuredName(data.NamedInsured    || '');
      setAddress(data.StreetAddress       || '');
      setCity(data.City                   || '');
      setStateName(data.State             || '');
      setZipCode(data.ZipCode             || '');
      setPolicyNumber(data.PolicyNumber   || '');
      const rawPremium: string = data.PolicyPremium ?? '';
      setPolicyPremium(
        rawPremium
          ? rawPremium.replace(/[,$]/g, '').replace(/\.00$/, '').trim()
          : ''
      );
      setEffectiveDate(data.EffectiveDate   || '');
      setExpirationDate(data.ExpirationDate || '');
      setCoverage(data.AutoMobileLiability  || null);
      setDrivers(data.DriverSchedule        || []);
      setVehicles(data.Vehicles             || []);
    } catch {
      setInsuredName(''); setAddress(''); setCity(''); setStateName(''); setZipCode('');
      setPolicyNumber(''); setPolicyPremium(''); setEffectiveDate(''); setExpirationDate('');
      setCoverage(null); setDrivers([]); setVehicles([]);
    }

    setShowPopup(true);
    setPreviewLoading(true);
    setPdfUrl('');

    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/MTMScan/previewdocument`,
      { TransactionId: row.submissionId, FileName: row.fileName, Target: row.documentType },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(res => setPdfUrl(res.data?.fileUrl || ''))
      .catch(() => showError('Error loading document preview.'))
      .finally(() => setPreviewLoading(false));
  }, [showError]);

  /* ── Feedback (per row in table) ── */
  const openFeedback = useCallback((row: HistoryRow, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackTxId(row.submissionId);
    setFeedbackText('');
    setFeedbackRating(row.isLiked ?? null);
    setFbError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackTxId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFbError('');
  }, []);

  const saveRating = useCallback((txId: string, isLiked: boolean | null, feedback: string) => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/MTMScan/isliked`,
      { TransactionId: txId, IsLiked: isLiked, Feedback: feedback ?? '' },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => { showSuccess('Feedback submitted successfully!'); closeFeedback(); }).catch(() => {});
  }, [closeFeedback, showSuccess]);

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = historyData.find(r => r.submissionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setFeedbackMap(prev => ({ ...prev, [feedbackTxId]: next }));
    setHistoryData(prev => prev.map(r => r.submissionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    saveRating(feedbackTxId, next, feedbackText);
  }, [feedbackTxId, historyData, feedbackText, saveRating, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = historyData.find(r => r.submissionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setFeedbackMap(prev => ({ ...prev, [feedbackTxId]: next }));
    setHistoryData(prev => prev.map(r => r.submissionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    saveRating(feedbackTxId, next, feedbackText);
  }, [feedbackTxId, historyData, feedbackText, saveRating, closeFeedback]);

  const onSubmitFeedback = useCallback(() => {
    if (!feedbackTxId) return;
    if (!feedbackText.trim()) { setFbError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFbError('Please provide your feedback within 250 characters'); return; }
    setFbError('');
    setFbSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/MTMScan/feedback`,
      { TransactionId: feedbackTxId, IsLiked: feedbackRating, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { setFbSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => { setFbSubmitting(false); });
  }, [feedbackTxId, feedbackText, feedbackRating, closeFeedback, showSuccess]);

  useEffect(() => {
    if (!feedbackTxId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackTxId, closeFeedback]);

  /* ── Upload PDF to AMS ── */
  const onUploadToAMS = useCallback(async () => {
    if (!uploadInsuredId) return;
    setUploadLoading(true);
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/uploadV1`,
        JSON.stringify({
          TransactionId: transactionId,
          FileName:      fileName,
          NCPolicyId:    '',
          NCInsuredId:   uploadInsuredId,
          FileData:      '',
          FolderName:    'MTMScan',
          TargetName:    'MTMScan',
          NowCertsToken: getNcToken(),
        }),
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
      );
      if (res.data?.success) showSuccess('PDF uploaded to AMS successfully.');
      else showError(res.data?.errorMessage || 'Error uploading to AMS.');
      setShowUploadDialog(false);
    } catch {
      showError('Error uploading PDF to AMS.');
    } finally {
      setUploadLoading(false);
    }
  }, [uploadInsuredId, transactionId, fileName, showSuccess, showError]);

  /* ── Helpers ── */
  const filteredData = historyData.filter(row => {
    if (!globalFilter.trim()) return true;
    const s = globalFilter.toLowerCase();
    return row.fileName?.toLowerCase().includes(s) || row.userName?.toLowerCase().includes(s);
  });

  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));

  const formatDate = (iso: string) => {
    if (!iso) return '';
    return moment.utc(iso).local().format('MM/DD/YYYY HH:mm');
  };

  const statusConfig = (s: string) => s?.toLowerCase() === 'success'
    ? STATUS_CONFIG.Success
    : STATUS_CONFIG.Failed;

  const skeletonRows = Array.from({ length: PAGE_SIZE }, (_, i) => i);

  /* ── Render ── */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexShrink: 0 }}>
        <div className="cs-client-search__input-wrap" style={{ flex: 1 }}>
          <FiSearch size={14} className="cs-client-search__icon" />
          <input
            className="cs-client-search__input"
            placeholder="Search by file name or user…"
            value={globalFilter}
            onChange={e => setGlobalFilter(e.target.value)}
          />
        </div>
        <button
          onClick={fetchHistory}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
            background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
            borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <FiRefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', borderRadius: 12, border: '1px solid var(--border-glass)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--surface-glass)', borderBottom: '1px solid var(--border-glass)' }}>
              {['File Name', 'Doc Type', 'Submitted Date', 'User', 'Status', 'Update in AMS', 'Actions', 'Feedback'].map(col => (
                <th key={col} style={{
                  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
                  color: 'var(--text-secondary)', fontSize: 11, letterSpacing: '0.04em',
                  textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? skeletonRows.map(i => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-glass)' }}>
                    {[1,2,3,4,5,6,7,8].map(j => (
                      <td key={j} style={{ padding: '10px 14px' }}>
                        <Skeleton width="80%" height="1rem" />
                      </td>
                    ))}
                  </tr>
                ))
              : filteredData.length === 0
                ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                      No history found.
                    </td>
                  </tr>
                )
                : filteredData.map((row, idx) => {
                    const s = statusConfig(row.status);
                    return (
                      <motion.tr
                        key={row.submissionId}
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.03, duration: 0.2 }}
                        style={{ borderBottom: '1px solid var(--border-glass)' }}
                      >
                        <td style={{ padding: '10px 14px', color: 'var(--text-primary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <span title={row.fileName}>{row.fileName}</span>
                        </td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontSize: 12 }}>
                          {row.documentType || '—'}
                        </td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontSize: 12 }}>
                          {formatDate(row.created)}
                        </td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>{row.userName}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span className="qp-status-pill" style={{ color: s.color }}>{s.label}</span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {row.updateInNowcerts
                            ? <span style={{
                                display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
                                borderRadius: 20, fontSize: 11, fontWeight: 600,
                                color: '#22c55e', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
                              }}>{row.updateInNowcerts}</span>
                            : <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>—</span>
                          }
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {row.status?.toLowerCase() === 'success' && (
                            <button
                              onClick={() => onPreviewClick(row)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                              title="Preview"
                            >
                              <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                            </button>
                          )}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                          <button
                            className="qp-table__action-btn"
                            title="Feedback"
                            onClick={e => openFeedback(row, e)}
                            style={{ margin: '0 auto' }}
                          >
                            <FiMessageSquare size={14} />
                          </button>
                        </td>
                      </motion.tr>
                    );
                  })
            }
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && totalRecords > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexShrink: 0, fontSize: 12, color: 'var(--text-secondary)' }}>
          <span>
            Showing {(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE, totalRecords)} of {totalRecords}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              style={{ background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 6, padding: '4px 10px', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1, color: 'var(--text-secondary)' }}
            >
              <FiChevronLeft size={13} />
            </button>
            <span style={{ padding: '4px 10px' }}>{page} / {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              style={{ background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 6, padding: '4px 10px', cursor: page >= totalPages ? 'default' : 'pointer', opacity: page >= totalPages ? 0.4 : 1, color: 'var(--text-secondary)' }}
            >
              <FiChevronRight size={13} />
            </button>
          </div>
        </div>
      )}

      {/* ── Feedback Popup ── */}
      {feedbackTxId && feedbackAnchor && (
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {fbError && <p className="csfb-error">{fbError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button
              className="csfb-btn-submit"
              disabled={fbSubmitting}
              onClick={onSubmitFeedback}
            >
              {fbSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {/* ── Preview Dialog ── */}
      {showPopup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            width: '88vw', height: '90vh', background: 'var(--bg-primary)',
            border: '1px solid var(--border-glass)', borderRadius: 14,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
          }}>
            {/* Dialog header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 20px', borderBottom: '1px solid var(--border-glass)',
              background: 'var(--surface-glass)', flexShrink: 0,
            }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                MTM Scan — Preview {activeRow?.fileName ? `· ${activeRow.fileName}` : ''}
              </span>
              <button onClick={() => setShowPopup(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                <FiX size={18} />
              </button>
            </div>

            {previewLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <span className="cs-upload-spinner" style={{ width: 32, height: 32 }} />
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>

                {/* Left: PDF */}
                {pdfUrl && (
                  <div style={{ width: '45%', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border-glass)' }}>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-glass)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Uploaded PDF</span>
                      <button
                        onClick={() => {
                          setShowUploadDialog(true);
                          setUploadClientSearch('');
                          setUploadClientName('');
                          setUploadClientList([]);
                          setUploadInsuredId('');
                        }}
                        style={{
                          padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                          background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                          color: 'var(--text-secondary)', fontFamily: 'inherit',
                        }}
                      >
                        Upload in AMS
                      </button>
                    </div>
                    <div style={{ flex: 1, minHeight: 0 }}>
                      <iframe src={pdfUrl} title="MTM PDF" style={{ width: '100%', height: '100%', border: 'none' }} />
                    </div>
                  </div>
                )}

                {/* Right: Data tabs */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  {/* Tab bar */}
                  <div style={{ display: 'flex', borderBottom: '1px solid var(--border-glass)', flexShrink: 0, background: 'var(--surface-glass)' }}>
                    {(['insured', 'coverage', 'drivers', 'vehicles'] as PreviewTab[]).map(key => (
                      <button
                        key={key}
                        onClick={() => setPreviewTab(key)}
                        style={{
                          padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: 12, fontWeight: previewTab === key ? 700 : 500,
                          color: previewTab === key ? 'var(--text-primary)' : 'var(--text-secondary)',
                          borderBottom: previewTab === key ? '2px solid var(--accent-purple, #8b5cf6)' : '2px solid transparent',
                          fontFamily: 'inherit', textTransform: 'capitalize',
                        }}
                      >
                        {key}
                      </button>
                    ))}
                  </div>

                  {/* Tab content */}
                  <div style={{ flex: 1, overflow: 'auto', padding: '14px 16px' }}>

                    {/* Insured */}
                    {previewTab === 'insured' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {[
                          ['Insured Name',    insuredName],
                          ['Address',         address],
                          ['City',            city],
                          ['State',           stateName],
                          ['Zip Code',        zipCode],
                          ['Policy Number',   policyNumber],
                          ['Policy Premium',  policyPremium],
                          ['Effective Date',  effectiveDate],
                          ['Expiration Date', expirationDate],
                        ].map(([label, val]) => (
                          <div key={label}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
                            <div style={{ fontSize: 13, color: 'var(--text-primary)', background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 6, padding: '6px 10px' }}>
                              {val || '—'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Coverage */}
                    {previewTab === 'coverage' && (
                      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border-glass)' }}>
                        {!coverage ? (
                          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No coverage data.</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: 'var(--surface-glass)', borderBottom: '1px solid var(--border-glass)' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>Coverage</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>Limit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(docType === 'Quote' ? [
                                ['Liability Limit',            coverage.LiabilityLimit            || coverage.liabilityLimit],
                                ['Deductible',                 coverage.Deductible                || coverage.deductible],
                                ['UM/BI Limit',                coverage.UMBILimit                 || coverage.umbiLimit],
                                ['UIM/BI Limit',               coverage.UIMBILimit                || coverage.uimbiLimit],
                                ['UM/PD Limit',                coverage.UMPDLimit                 || coverage.umpdLimit],
                                ['UIM/PD Limit',               coverage.UIMPDLimit                || coverage.uimpdLimit],
                                ['Medical Payments',           coverage.MedicalPaymentsLimit       || coverage.medicalPaymentsLimit],
                                ['PIP',                        coverage.PersonalInjuryProtectionLimit || coverage.personalInjuryProtectionLimit],
                                ['Auto Coverage Symbol',       coverage.AutoCoverageSymbol         || coverage.autoCoverageSymbol],
                              ] : [
                                ['BI / PD Limit',              coverage.BodilyInjuryPropertyDamageLimit || coverage.bodilyInjuryPropertyDamageLimit],
                                ['Liability Limit',            coverage.LiabilityLimit                  || coverage.liabilityLimit],
                                ['Covered Autos — Liability',  coverage.CoveredAutosSymbolLiability     || coverage.coveredAutosSymbolLiability],
                                ['Covered Autos — Med Payments', coverage.CoveredAutosSymbolAutoMedicalPayments || coverage.coveredAutosSymbolAutoMedicalPayments],
                                ['Covered Autos — PIP',        coverage.CoveredAutosSymbolPIP           || coverage.coveredAutosSymbolPIP],
                                ['Covered Autos — UM/UIM',     coverage.CoveredAutosSymbolUMandorUIM    || coverage.coveredAutosSymbolUMandorUIM],
                                ['UM/BI Limit',                coverage.UMBILimit                       || coverage.umbiLimit],
                                ['UIM/BI Limit',               coverage.UIMBILimit                      || coverage.uimbiLimit],
                                ['Medical Payments',           coverage.MedicalPaymentsLimit             || coverage.medicalPaymentsLimit],
                                ['PIP Limits',                 coverage.PersonalInjuryProtectionLimits  || coverage.personalInjuryProtectionLimits],
                                ['UIIA',                       coverage.UIIA                            || coverage.uiia],
                              ]).map(([label, val], i) => (
                                <tr key={String(label)} style={{ borderBottom: '1px solid var(--border-glass)', background: i % 2 !== 0 ? 'rgba(255,255,255,0.015)' : 'transparent' }}>
                                  <td style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</td>
                                  <td style={{ padding: '8px 12px', color: 'var(--text-primary)' }}>{val || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}

                    {/* Drivers */}
                    {previewTab === 'drivers' && (
                      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border-glass)' }}>
                        {drivers.length === 0 ? (
                          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No drivers.</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: 'var(--surface-glass)', borderBottom: '1px solid var(--border-glass)' }}>
                                {['License #', 'DOB', 'Name', 'Excluded'].map(col => (
                                  <th key={col} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {drivers.map((d: any, i: number) => {
                                const licNo    = d.LicenseNumber || d.licenseNumber || '—';
                                const dob      = d.DOB           || d.Dob           || d.dob           || '—';
                                const drvName  = d.Name          || d.name          || '—';
                                const excl     = d.Excluded      || d.excluded      || '';
                                const isExcluded = excl === 'Y' || excl === true;
                                return (
                                  <tr key={i} style={{ borderBottom: '1px solid var(--border-glass)', background: i % 2 !== 0 ? 'rgba(255,255,255,0.015)' : 'transparent' }}>
                                    <td style={{ padding: '8px 12px', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 12 }}>{licNo}</td>
                                    <td style={{ padding: '8px 12px', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{dob}</td>
                                    <td style={{ padding: '8px 12px', color: 'var(--text-primary)' }}>{drvName}</td>
                                    <td style={{ padding: '8px 12px' }}>
                                      <span style={{
                                        display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
                                        borderRadius: 12, fontSize: 11, fontWeight: 600,
                                        color:      isExcluded ? '#ef4444' : '#22c55e',
                                        background: isExcluded ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                                        border: `1px solid ${isExcluded ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.25)'}`,
                                      }}>
                                        {isExcluded ? 'Yes' : 'No'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}

                    {/* Vehicles */}
                    {previewTab === 'vehicles' && (
                      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border-glass)' }}>
                        {vehicles.length === 0 ? (
                          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No vehicles.</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: 'var(--surface-glass)', borderBottom: '1px solid var(--border-glass)' }}>
                                {['VIN', 'Make', 'Year'].map(col => (
                                  <th key={col} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>{col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {vehicles.map((v: any, i: number) => (
                                <tr key={i} style={{ borderBottom: '1px solid var(--border-glass)', background: i % 2 !== 0 ? 'rgba(255,255,255,0.015)' : 'transparent' }}>
                                  <td style={{ padding: '8px 12px', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 12 }}>{v.VIN  || v.Vin  || v.vin  || '—'}</td>
                                  <td style={{ padding: '8px 12px', color: 'var(--text-primary)' }}>{v.Make || v.make || '—'}</td>
                                  <td style={{ padding: '8px 12px', color: 'var(--text-primary)' }}>{v.Year || v.year || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Upload in AMS Dialog ── */}
      {showUploadDialog && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            width: 400, background: 'var(--bg-primary)', border: '1px solid var(--border-glass)',
            borderRadius: 12, padding: '24px', boxShadow: '0 16px 60px rgba(0,0,0,0.5)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>Upload in AMS</span>
              <button onClick={() => setShowUploadDialog(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                <FiX size={16} />
              </button>
            </div>

            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Search Client</p>
            <div style={{ position: 'relative' }}>
              <div className="cs-client-search__input-wrap">
                <FiSearch size={13} className="cs-client-search__icon" />
                <input
                  className="cs-client-search__input"
                  placeholder={uploadClientName || 'Enter client name…'}
                  value={uploadClientSearch}
                  onChange={e => {
                    setUploadClientSearch(e.target.value);
                    setUploadClientName(e.target.value);
                    if (!e.target.value) { setUploadInsuredId(''); setUploadClientList([]); }
                  }}
                />
                {uploadClientLoading && <span className="cs-client-search__spinner" />}
              </div>
              {uploadClientSearch.trim() && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                  background: 'var(--bg-primary)', border: '1px solid var(--border-glass)',
                  borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', maxHeight: 200, overflowY: 'auto',
                }}>
                  {uploadClientLoading ? (
                    <div style={{ padding: 12, display: 'flex', justifyContent: 'center' }}><span className="cs-client-search__spinner" /></div>
                  ) : uploadClientList.length === 0 ? (
                    <div style={{ padding: '10px 14px', color: 'var(--text-tertiary)', fontSize: 12 }}>No results found</div>
                  ) : (
                    uploadClientList.map((c: any) => (
                      <div
                        key={c.databaseId}
                        style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-glass)' }}
                        onClick={() => {
                          setUploadInsuredId(c.databaseId);
                          setUploadClientName(c.name);
                          setUploadClientSearch('');
                          setUploadClientList([]);
                        }}
                      >
                        {c.name}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="cs-btn-cancel" onClick={() => setShowUploadDialog(false)}>Cancel</button>
              {uploadLoading ? (
                <span className="cs-upload-spinner" />
              ) : (
                <motion.button
                  className="cs-btn-start"
                  disabled={!uploadInsuredId}
                  whileHover={{ scale: uploadInsuredId ? 1.03 : 1 }}
                  onClick={onUploadToAMS}
                  style={{ fontSize: 12, padding: '8px 20px' }}
                >
                  Upload
                </motion.button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MTMScanHistory;

